import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Analytics as AnalyticsIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Assignment as AssignmentIcon,
  DateRange as DateRangeIcon,
  BarChart as BarChartIcon,
  PieChart as PieChartIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';

function FormAnalytics({ darkMode }) {
  const { formId } = useParams();
  const [analytics, setAnalytics] = useState(null);
  const [fieldAnalytics, setFieldAnalytics] = useState({});
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState('json');
  const [submissionDetailDialog, setSubmissionDetailDialog] = useState(null);

  useEffect(() => {
    loadAnalytics();
    loadSubmissions();
  }, [formId]);

  const loadAnalytics = async () => {
    try {
      const response = await fetch(`/api/v1/form-runtime/form/${formId}/analytics`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('tkn')}`,
        },
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setAnalytics(data.analytics);
        setFieldAnalytics(data.fieldAnalytics);
      }
    } catch (error) {
      console.error('Error loading analytics:', error);
    }
  };

  const loadSubmissions = async () => {
    try {
      const response = await fetch(`/api/v1/form-runtime/form/${formId}/submissions`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('tkn')}`,
        },
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setSubmissions(data.submissions);
      }
    } catch (error) {
      console.error('Error loading submissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const url = `/api/v1/form-runtime/form/${formId}/export?format=${exportFormat}`;
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('tkn')}`,
        },
        credentials: 'include',
      });

      if (response.ok) {
        if (exportFormat === 'csv') {
          const blob = await response.blob();
          const downloadUrl = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = downloadUrl;
          a.download = `form-data-${formId}.csv`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(downloadUrl);
        } else {
          const data = await response.json();
          const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
          const downloadUrl = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = downloadUrl;
          a.download = `form-data-${formId}.json`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(downloadUrl);
        }
      }
    } catch (error) {
      console.error('Error exporting data:', error);
    }
    setExportDialogOpen(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'submitted': return 'primary';
      case 'processing': return 'warning';
      case 'completed': return 'success';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6">Loading Analytics...</Typography>
        <LinearProgress sx={{ mt: 2 }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{
        display: 'flex', justifyContent: 'between', alignItems: 'center', mb: 3,
      }}
      >
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AnalyticsIcon />
          {' '}
          Form Analytics
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => { loadAnalytics(); loadSubmissions(); }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={() => setExportDialogOpen(true)}
          >
            Export Data
          </Button>
        </Box>
      </Box>

      {/* Overview Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <PeopleIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
              <Typography variant="h4">{analytics?.totalSubmissions || 0}</Typography>
              <Typography color="text.secondary">Total Submissions</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <TrendingUpIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
              <Typography variant="h4">
                {analytics?.averageSubmissionSize ? `${(analytics.averageSubmissionSize / 1024).toFixed(1)}KB` : '0KB'}
              </Typography>
              <Typography color="text.secondary">Avg. Data Size</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <DateRangeIcon sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
              <Typography variant="h6">
                {analytics?.firstSubmission
                  ? new Date(analytics.firstSubmission).toLocaleDateString()
                  : 'No data'}
              </Typography>
              <Typography color="text.secondary">First Submission</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <AssignmentIcon sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
              <Typography variant="h6">
                {analytics?.lastSubmission
                  ? new Date(analytics.lastSubmission).toLocaleDateString()
                  : 'No data'}
              </Typography>
              <Typography color="text.secondary">Latest Submission</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Field Analytics */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography
              variant="h6"
              sx={{
                mb: 2, display: 'flex', alignItems: 'center', gap: 1,
              }}
            >
              <BarChartIcon />
              {' '}
              Field Response Rates
            </Typography>

            {Object.entries(fieldAnalytics).map(([fieldId, stats]) => (
              <Box key={fieldId} sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                    {stats.label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {stats.responseRate}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={parseFloat(stats.responseRate)}
                  sx={{ height: 8, borderRadius: 4 }}
                />
                <Typography variant="caption" color="text.secondary">
                  {stats.totalResponses}
                  {' '}
                  responses •
                  {stats.uniqueValues}
                  {' '}
                  unique values
                </Typography>
              </Box>
            ))}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography
              variant="h6"
              sx={{
                mb: 2, display: 'flex', alignItems: 'center', gap: 1,
              }}
            >
              <PieChartIcon />
              {' '}
              Field Statistics
            </Typography>

            <List dense>
              {Object.entries(fieldAnalytics).map(([fieldId, stats]) => (
                <ListItem key={fieldId} divider>
                  <ListItemText
                    primary={stats.label}
                    secondary={(
                      <Box>
                        <Typography variant="caption" component="div">
                          Type:
                          {' '}
                          {stats.type}
                          {' '}
                          • Responses:
                          {' '}
                          {stats.totalResponses}
                        </Typography>
                        {stats.average && (
                          <Typography variant="caption" color="primary">
                            Average:
                            {' '}
                            {stats.average}
                          </Typography>
                        )}
                        {stats.mostCommon && (
                          <Typography variant="caption" color="success.main">
                            Most common:
                            {' '}
                            {stats.mostCommon}
                          </Typography>
                        )}
                      </Box>
                    )}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* Recent Submissions */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Recent Submissions</Typography>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Submission ID</TableCell>
                    <TableCell>Submitted By</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {submissions.slice(0, 10).map((submission) => (
                    <TableRow key={submission._id}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {submission._id.slice(-8)}
                        </Typography>
                      </TableCell>
                      <TableCell>{submission.metadata?.submittedBy || 'Unknown'}</TableCell>
                      <TableCell>
                        {new Date(submission.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={submission.status}
                          color={getStatusColor(submission.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => setSubmissionDetailDialog(submission)}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Export Dialog */}
      <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)}>
        <DialogTitle>Export Form Data</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Export Format</InputLabel>
            <Select
              value={exportFormat}
              onChange={(e) => setExportFormat(e.target.value)}
              label="Export Format"
            >
              <MenuItem value="json">JSON Format</MenuItem>
              <MenuItem value="csv">CSV Format</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleExport} variant="contained">Export</Button>
        </DialogActions>
      </Dialog>

      {/* Submission Detail Dialog */}
      {submissionDetailDialog && (
        <Dialog
          open={Boolean(submissionDetailDialog)}
          onClose={() => setSubmissionDetailDialog(null)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Submission Details</DialogTitle>
          <DialogContent>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2">Submission ID:</Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                {submissionDetailDialog._id}
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2">Submitted Data:</Typography>
              <Paper sx={{ p: 2, bgcolor: 'background.default', mt: 1 }}>
                <pre style={{ margin: 0, fontSize: '14px', whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(submissionDetailDialog.submittedData, null, 2)}
                </pre>
              </Paper>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2">Metadata:</Typography>
              <Typography variant="body2">
                Submitted by:
                {' '}
                {submissionDetailDialog.metadata?.submittedBy}
                <br />
                Date:
                {' '}
                {new Date(submissionDetailDialog.createdAt).toLocaleString()}
                <br />
                Status:
                {' '}
                {submissionDetailDialog.status}
                <br />
                Size:
                {' '}
                {JSON.stringify(submissionDetailDialog.submittedData).length}
                {' '}
                bytes
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSubmissionDetailDialog(null)}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
}

export default FormAnalytics;
